import { useRouter } from "next/router";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import api, { ApiCatalog } from "@/api";
import { ApiResponseToCsvGenerationRequest } from "@/directory/types";
import { User } from "@/types/models/User/v3/User.interface";
import { useGetUser } from "@/users/v3";
import { DirectoryType } from "@/admin/types";

export interface DirectoryPageAuxiliaryDataContextInterface {
  userData: User | undefined | null;
  userDataIsLoading: boolean;
  userDataFetchFailed: boolean;
  refetchStatusToGenerateCsv: () => void;
  responseFromStatusApi: ApiResponseToCsvGenerationRequest | undefined;
  refetchingStatusApiResponse: boolean;
}

const initialContext: DirectoryPageAuxiliaryDataContextInterface = {
  userData: {} as unknown as User,
  userDataIsLoading: false,
  userDataFetchFailed: false,
  refetchStatusToGenerateCsv: () => null,
  responseFromStatusApi: undefined,
  refetchingStatusApiResponse: false,
};

const DirectoryPageAuxiliaryDataContext =
  createContext<DirectoryPageAuxiliaryDataContextInterface>(initialContext);

// This context provider fetches the data needed to populate directory tables and to inform the CSV generation status also found on directory pages.
export const DirectoryPageAuxiliaryDataProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [enableCallToStatusEndpoint, setEnableCallToStatusEndpoint] =
    useState(false);
  // Get the directory type from the page url in order to retrieve the csv-generation status for the current directory.
  const { pathname } = useRouter() || {};
  const splitPathname = pathname?.split("/");

  const directoryTypeInUrl = splitPathname[splitPathname.length - 1];
  const isValidDirectoryType = Object.values(DirectoryType).includes(
    directoryTypeInUrl as DirectoryType,
  );

  const {
    data: userData,
    isLoading: userDataIsLoading,
    isError: userDataFetchFailed,
  } = useGetUser();

  const userUniqueId = userData?.unique_id;

  useEffect(() => {
    if (userUniqueId && isValidDirectoryType && !userDataIsLoading) {
      setEnableCallToStatusEndpoint(true);
    }
  }, [userDataIsLoading, directoryTypeInUrl]);

  // Fetch the status of the user's most recent request to download a directory csv.
  const {
    data: responseFromStatusApi,
    isRefetching: refetchingStatusApiResponse,
    refetch: refetchStatusToGenerateCsv,
  }: UseQueryResult<ApiResponseToCsvGenerationRequest, Error> = useQuery({
    enabled: enableCallToStatusEndpoint,
    queryKey: [ApiCatalog.adminConsoleDirectoryCsvGenerationStatus.key],
    queryFn: async () =>
      await api().get(
        ApiCatalog.adminConsoleDirectoryCsvGenerationStatus.endpoint,
        {
          urlReplacers: [{ userUniqueId, directoryType: directoryTypeInUrl }],
        },
      ),
  });

  const sharedContext: DirectoryPageAuxiliaryDataContextInterface = {
    userData,
    userDataIsLoading,
    userDataFetchFailed,
    refetchStatusToGenerateCsv,
    responseFromStatusApi,
    refetchingStatusApiResponse,
  };

  return (
    <DirectoryPageAuxiliaryDataContext.Provider value={sharedContext}>
      {children}
    </DirectoryPageAuxiliaryDataContext.Provider>
  );
};

const useDirectoryPageAuxiliaryDataContext =
  (): DirectoryPageAuxiliaryDataContextInterface => {
    const directoryPageAuxiliaryDataContext = useContext(
      DirectoryPageAuxiliaryDataContext,
    );

    if (directoryPageAuxiliaryDataContext === undefined) {
      throw new Error(
        "DirectoryPageAuxiliaryDataContext must be used within a DirectoryPageAuxiliaryDataProvider.",
      );
    }

    return directoryPageAuxiliaryDataContext;
  };

export default useDirectoryPageAuxiliaryDataContext;
